<script setup>
// The site footer: brand, in-page anchors mirrored from the header nav, the two public
// content pages, account actions, and the legal links.
//
// Shared with the public content pages (via MarketingShell), which is why the section
// links are RouterLinks to "/#section" rather than bare "#section": on the landing page
// anchorScroll intercepts them and scrolls; anywhere else it stands aside and the
// router navigates home to that section. See lib/scroll.js.
import { useI18n } from "vue-i18n";
import BrandMark from "@/components/BrandMark.vue";
import { anchorScroll } from "@/lib/scroll";
import { openCookieNotice } from "@/lib/analytics";

const { t } = useI18n();
const year = new Date().getFullYear();

const sections = [
  { to: "/#hoe-het-werkt", key: "landing.footer.how" },
  { to: "/#geldstroom", key: "landing.footer.flows" },
  { to: "/#prijzen", key: "landing.footer.pricing" },
  { to: "/#vragen", key: "landing.footer.faq" },
];
</script>

<template>
  <footer class="border-t-2 border-foreground bg-card">
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-5 py-10 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-2.5">
        <BrandMark class="size-6 text-primary" />
        <div>
          <p class="font-sans text-base font-bold tracking-tight text-foreground">Pandig</p>
          <p class="text-xs text-muted-foreground">{{ t("landing.footer.tagline") }}</p>
        </div>
      </div>
      <nav class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground" aria-label="Footer">
        <RouterLink v-for="item in sections" :key="item.to" :to="item.to" class="hover:text-foreground" @click="anchorScroll">
          {{ t(item.key) }}
        </RouterLink>
        <RouterLink to="/voor-vve-beheerders" class="hover:text-foreground">{{ t("landing.footer.managers") }}</RouterLink>
        <RouterLink to="/vve-betalingsachterstand" class="hover:text-foreground">{{ t("landing.footer.arrears") }}</RouterLink>
        <RouterLink to="/login" class="hover:text-foreground">{{ t("landing.footer.sign_in") }}</RouterLink>
        <RouterLink to="/signup" class="font-semibold text-primary hover:underline">{{ t("landing.footer.sign_up") }}</RouterLink>
      </nav>
    </div>
    <div class="border-t-2 border-foreground/10">
      <!-- The full company identity, visibly reachable: for a product that handles a
           VvE's payment flows, a named entity, address and a plain email matter. -->
      <div class="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
        <span class="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-muted-foreground">
          &copy; {{ year }} Pandig B.V. · Papenstraat 35, 7411 NA Deventer ·
          <a href="mailto:info@pandig.nl" class="normal-case hover:text-foreground">info@pandig.nl</a> ·
          <a href="https://wa.me/31643500374" class="normal-case hover:text-foreground">WhatsApp: +31 6 43500374</a> ·
          <a href="sms:+31643500374" class="normal-case hover:text-foreground">sms</a> ·
          <a href="tel:+31643500374" class="normal-case hover:text-foreground">bel</a>
        </span>
        <nav class="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-muted-foreground" aria-label="Juridisch">
          <RouterLink to="/algemene-voorwaarden" class="hover:text-foreground">{{ t("landing.footer.terms") }}</RouterLink>
          <RouterLink to="/privacybeleid" class="hover:text-foreground">{{ t("landing.footer.privacy") }}</RouterLink>
          <!-- Withdrawing consent has to be as reachable as giving it was, so it sits
               in the same row as the legal links on every public page. -->
          <button type="button" class="uppercase tracking-[0.08em] hover:text-foreground" @click="openCookieNotice">{{ t("landing.footer.cookies") }}</button>
        </nav>
      </div>
    </div>
  </footer>
</template>
